'use client';

import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '@/lib/ui/genie';

/** How long each dot sits before the next one lands. */
const STEP_MS = 400;

/** Dots in the cycle: "Connecting." → "Connecting.." → "Connecting..." → round again. */
const MAX_DOTS = 3;

/**
 * The composer placeholder while a voice session negotiates.
 *
 * The mic glyph already turns into a spinner, but on a phone the thumb is
 * usually sitting on top of it and a 20px spinner is easy to miss — people tap
 * again, which does nothing, and it reads as a dead button. The placeholder is
 * the widest thing on the row and the eye is already there, so it says what is
 * happening in words.
 *
 * Returns `null` when nothing is connecting, so a call site can fall straight
 * through to its normal placeholder with `??`.
 */
export function useConnectingLabel(active: boolean, word = 'Connecting'): string | null {
  const [dots, setDots] = useState(MAX_DOTS);

  useEffect(() => {
    if (!active) return;
    // Cycling text is motion like any other. Someone who has asked for less of
    // it still gets the word and a settled ellipsis — the placeholder says the
    // same thing, it just doesn't dance.
    if (prefersReducedMotion()) {
      setDots(MAX_DOTS);
      return;
    }
    setDots(1);
    const id = window.setInterval(() => setDots(d => (d % MAX_DOTS) + 1), STEP_MS);
    return () => window.clearInterval(id);
  }, [active]);

  return active ? `${word}${'.'.repeat(dots)}` : null;
}
